import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import type { CardId } from 'src/replicant/cards/cards.ruleset';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { Avatar } from 'src/game/components/shared/Avatar';
import { Card } from './Card';

const PANEL_IMAGE = 'assets/ui/shared/frames/scroller_panel_blue.png';
const CARD_BACK_IMAGE = 'assets/ui/cards/common/card_back.png';
const TRADE_ICON_IMAGE = 'assets/ui/cards/common/icon_trade.png';
const REJECT_ICON_IMAGE = 'assets/ui/cards/common/icon_x.png';
const ITEM_WIDTH = 510;
const ITEM_HEIGHT = 220;

export type CardsTradeItemState =
  | 'sent'
  | 'sentCountered'
  | 'received'
  | 'receivedCountered';

const STATE_LABEL_KEYS: Record<CardsTradeItemState, string> = {
  sent: 'cards.cardTrade.states.sent',
  sentCountered: 'cards.cardTrade.states.sentCountered',
  received: 'cards.cardTrade.states.received',
  receivedCountered: 'cards.cardTrade.states.receivedCountered',
};

export const CARDS_TRADE_ITEM_ASSETS = [
  PANEL_IMAGE,
  CARD_BACK_IMAGE,
  TRADE_ICON_IMAGE,
  REJECT_ICON_IMAGE,
  GENERIC_AVATAR,
];

export type CardsTradeItemProps = {
  sendCardId?: CardId;
  receiveCardId?: CardId;
  otherUserName?: string;
  otherUserAvatar?: string;
  tradeState: CardsTradeItemState;
  onAccept: () => void | Promise<void>;
  onTrade: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
};

export class CardsTradeItem extends View {
  private playerAvatar: Avatar;
  private playerName: LangBitmapFontTextView;
  private stateLabel: LangBitmapFontTextView;
  private card: Card;
  private waitingLabel: LangBitmapFontTextView;
  private acceptButton: ButtonScaleViewWithText;
  private tradeButton: ButtonScaleViewWithText;
  private rejectButton: ButtonScaleViewWithText;
  private onAccept: () => void | Promise<void> = () => undefined;
  private onTrade: () => void | Promise<void> = () => undefined;
  private onReject: () => void | Promise<void> = () => undefined;

  constructor(opts: ViewOpts = {}) {
    super({
      tag: 'item',
      width: ITEM_WIDTH,
      height: ITEM_HEIGHT,
      ...opts,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: this,
      image: PANEL_IMAGE,
      scaleMethod: '9slice',
      width: ITEM_WIDTH,
      height: ITEM_HEIGHT,
      x: ((opts.superview?.style.width ?? ITEM_WIDTH) - ITEM_WIDTH) * 0.5,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    this.card = new Card({
      superview: this,
      x: 35,
      y: 25,
      scale: 0.7,
    });

    this.playerAvatar = new Avatar({
      superview: this,
      x: 180,
      y: 30,
      width: 80,
      height: 80,
      centerOnOrigin: false,
      image: GENERIC_AVATAR,
    });

    this.playerName = new LangBitmapFontTextView({
      tag: 'friend-name',
      superview: this,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: this.playerAvatar.style.x + 90,
      y: this.playerAvatar.style.y,
      size: 26,
      width: 230,
      height: 40,
      font: 'Body',
    });

    this.stateLabel = new LangBitmapFontTextView({
      tag: 'trade-state',
      superview: this,
      align: 'left',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: true,
      x: this.playerAvatar.style.x + 90,
      y: this.playerAvatar.style.y + 40,
      size: 20,
      width: 230,
      height: 40,
      font: 'Body',
    });

    this.waitingLabel = new LangBitmapFontTextView({
      tag: 'trade-waiting',
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: true,
      x: 180,
      y: 120,
      size: 24,
      width: 310,
      height: 70,
      font: 'Body',
      visible: false,
      localeText: () => i18n('cards.cardTrade.waitingForResponse'),
    });

    this.tradeButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this,
      font: 'Body',
      fontSize: 30,
      labelOffsetX: 18,
      labelOffsetY: -3,
      lineSpacing: -8,
      x: 180,
      y: 120,
      width: 220,
      height: 70,
      visible: false,
      localeText: () => i18n('cards.cardTrade.propose'),
      icon: {
        image: TRADE_ICON_IMAGE,
        width: 34,
        height: 38,
        offset: { x: -55, y: -2 },
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.onTrade();
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.COUNTER,
      },
    });

    this.acceptButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this,
      font: 'Body',
      fontSize: 30,
      labelOffsetX: 18,
      labelOffsetY: -3,
      lineSpacing: -8,
      x: 180,
      y: 120,
      width: 220,
      height: 70,
      visible: false,
      localeText: () => i18n('cards.cardTrade.accept'),
      icon: {
        image: TRADE_ICON_IMAGE,
        width: 34,
        height: 38,
        offset: { x: -55, y: -2 },
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.onAccept();
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.ACCEPT,
      },
    });

    this.rejectButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this,
      font: 'Body',
      fontSize: 1,
      labelOffsetY: -2,
      x: 410,
      y: 120,
      width: 80,
      height: 70,
      visible: false,
      localeText: () => '',
      icon: {
        image: REJECT_ICON_IMAGE,
        width: 35,
        height: 36,
        offset: { x: 0, y: -2 },
      },
      onClick: async () => {
        sounds.sfx.play('cancel');
        await this.onReject();
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REJECT,
      },
    });
  }

  public setup(props: CardsTradeItemProps) {
    void this.playerAvatar.updateImage(props.otherUserAvatar || GENERIC_AVATAR);
    this.playerName.localeText = () => props.otherUserName ?? '';
    const displayCardId = props.receiveCardId ?? props.sendCardId;
    if (displayCardId) {
      this.card.show();
      this.card.setState({ id: displayCardId, count: 1 });
    } else {
      this.card.hide();
    }

    this.onAccept = props.onAccept;
    this.onTrade = props.onTrade;
    this.onReject = props.onReject;

    this.updateStateLabel(props.tradeState);
    this.updateActionButtons(props.tradeState);
  }

  private updateStateLabel(state: CardsTradeItemState) {
    this.stateLabel.localeText = () => i18n(STATE_LABEL_KEYS[state]);
  }

  private updateActionButtons(state: CardsTradeItemState) {
    this.acceptButton.hide();
    this.tradeButton.hide();
    this.rejectButton.hide();
    this.waitingLabel.hide();

    if (state === 'sentCountered') {
      this.acceptButton.show();
      this.rejectButton.show();
      return;
    }

    if (state === 'received') {
      this.tradeButton.show();
      this.rejectButton.show();
      return;
    }

    this.waitingLabel.show();
  }
}
